import {
  ApiProjectIdSchema,
  ArtifactIdSchema,
  ProjectFileIdSchema,
  ProjectIdSchema,
  ThreadIdSchema,
  WorkspaceIdSchema,
} from "@papersflow/contracts";
import { Schema } from "effect";
import type { Id } from "../../../backend/convex/_generated/dataModel";

export const CANONICAL_PROJECT_ROUTE_PATHS = {
  projects: "/w/$workspaceId/projects",
  project: "/w/$workspaceId/projects/$projectId",
  chat: "/w/$workspaceId/projects/$projectId/chats/$threadId",
  artifact: "/w/$workspaceId/projects/$projectId/artifacts/$artifactId",
  file: "/w/$workspaceId/projects/$projectId/files/$fileId",
} as const;

export const CANONICAL_CONSOLE_ROUTE_PATHS = {
  overview: "/console/$workspaceId",
  projects: "/console/$workspaceId/projects",
  project: "/console/$workspaceId/projects/$apiProjectId",
  apiKeys: "/console/$workspaceId/api-keys",
  usage: "/console/$workspaceId/usage",
  logs: "/console/$workspaceId/logs",
  models: "/console/$workspaceId/models",
  webhooks: "/console/$workspaceId/webhooks",
  team: "/console/$workspaceId/team",
  billing: "/console/$workspaceId/billing",
  settings: "/console/$workspaceId/settings",
} as const;

const WorkspaceRouteParamsSchema = Schema.Struct({ workspaceId: WorkspaceIdSchema });
const ProjectRouteParamsSchema = Schema.Struct({
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
});
const ChatRouteParamsSchema = Schema.Struct({
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  threadId: ThreadIdSchema,
});
const ArtifactRouteParamsSchema = Schema.Struct({
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  artifactId: ArtifactIdSchema,
});
const FileRouteParamsSchema = Schema.Struct({
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  fileId: ProjectFileIdSchema,
});
const ProjectItemRouteSearchSchema = Schema.Struct({
  threadId: Schema.optionalKey(ThreadIdSchema),
});
const ConsoleApiProjectRouteParamsSchema = Schema.Struct({
  workspaceId: WorkspaceIdSchema,
  apiProjectId: ApiProjectIdSchema,
});

const decodeWorkspaceRouteParams = Schema.decodeUnknownSync(WorkspaceRouteParamsSchema);
const decodeProjectRouteParams = Schema.decodeUnknownSync(ProjectRouteParamsSchema);
const decodeChatRouteParams = Schema.decodeUnknownSync(ChatRouteParamsSchema);
const decodeArtifactRouteParams = Schema.decodeUnknownSync(ArtifactRouteParamsSchema);
const decodeFileRouteParams = Schema.decodeUnknownSync(FileRouteParamsSchema);
const decodeProjectItemRouteSearch = Schema.decodeUnknownSync(ProjectItemRouteSearchSchema);
const decodeConsoleApiProjectRouteParams = Schema.decodeUnknownSync(
  ConsoleApiProjectRouteParamsSchema,
);

export interface WorkspaceRouteParams {
  readonly workspaceId: Id<"workspaces">;
}

export interface ProjectRouteParams extends WorkspaceRouteParams {
  readonly projectId: Id<"projects">;
}

export interface ChatRouteParams extends ProjectRouteParams {
  readonly threadId: Id<"threads">;
}

export interface ArtifactRouteParams extends ProjectRouteParams {
  readonly artifactId: Id<"artifacts">;
}

export interface FileRouteParams extends ProjectRouteParams {
  readonly fileId: Id<"projectFiles">;
}

export interface ProjectItemRouteSearch {
  readonly threadId?: Id<"threads">;
}

export interface ConsoleApiProjectRouteParams extends WorkspaceRouteParams {
  readonly apiProjectId: Id<"apiProjects">;
}

function convexWorkspaceId(value: string): Id<"workspaces"> {
  // @ts-expect-error -- Canonical Effect ID validation is complete; this route boundary adapts only the compile-time brand.
  return value;
}

function convexProjectId(value: string): Id<"projects"> {
  // @ts-expect-error -- Canonical Effect ID validation is complete; this route boundary adapts only the compile-time brand.
  return value;
}

function convexApiProjectId(value: string): Id<"apiProjects"> {
  // @ts-expect-error -- Canonical Effect API-project validation is complete; this route boundary adapts only the compile-time Convex brand.
  return value;
}

function convexThreadId(value: string): Id<"threads"> {
  // @ts-expect-error -- Canonical Effect ID validation is complete; this route boundary adapts only the compile-time brand.
  return value;
}

function convexArtifactId(value: string): Id<"artifacts"> {
  // @ts-expect-error -- Canonical Effect ID validation is complete; this route boundary adapts only the compile-time brand.
  return value;
}

function convexProjectFileId(value: string): Id<"projectFiles"> {
  // @ts-expect-error -- Canonical Effect ID validation is complete; this route boundary adapts only the compile-time brand.
  return value;
}

export const stringifyWorkspaceRouteParams = (params: WorkspaceRouteParams) => params;
export const stringifyProjectRouteParams = (params: ProjectRouteParams) => params;
export const stringifyChatRouteParams = (params: ChatRouteParams) => params;
export const stringifyArtifactRouteParams = (params: ArtifactRouteParams) => params;
export const stringifyFileRouteParams = (params: FileRouteParams) => params;
export const stringifyConsoleApiProjectRouteParams = (params: ConsoleApiProjectRouteParams) =>
  params;

export function parseWorkspaceRouteParams(params: { readonly workspaceId: string }) {
  try {
    const decoded = decodeWorkspaceRouteParams(params);
    return { workspaceId: convexWorkspaceId(decoded.workspaceId) };
  } catch {
    return false;
  }
}

export function parseProjectRouteParams(params: {
  readonly workspaceId: string;
  readonly projectId: string;
}) {
  try {
    const decoded = decodeProjectRouteParams(params);
    return {
      workspaceId: convexWorkspaceId(decoded.workspaceId),
      projectId: convexProjectId(decoded.projectId),
    };
  } catch {
    return false;
  }
}

export function parseChatRouteParams(params: {
  readonly workspaceId: string;
  readonly projectId: string;
  readonly threadId: string;
}) {
  try {
    const decoded = decodeChatRouteParams(params);
    return {
      workspaceId: convexWorkspaceId(decoded.workspaceId),
      projectId: convexProjectId(decoded.projectId),
      threadId: convexThreadId(decoded.threadId),
    };
  } catch {
    return false;
  }
}

export function parseArtifactRouteParams(params: {
  readonly workspaceId: string;
  readonly projectId: string;
  readonly artifactId: string;
}) {
  try {
    const decoded = decodeArtifactRouteParams(params);
    return {
      workspaceId: convexWorkspaceId(decoded.workspaceId),
      projectId: convexProjectId(decoded.projectId),
      artifactId: convexArtifactId(decoded.artifactId),
    };
  } catch {
    return false;
  }
}

export function parseFileRouteParams(params: {
  readonly workspaceId: string;
  readonly projectId: string;
  readonly fileId: string;
}) {
  try {
    const decoded = decodeFileRouteParams(params);
    return {
      workspaceId: convexWorkspaceId(decoded.workspaceId),
      projectId: convexProjectId(decoded.projectId),
      fileId: convexProjectFileId(decoded.fileId),
    };
  } catch {
    return false;
  }
}

export function parseProjectItemRouteSearch(search: Record<string, unknown>) {
  try {
    const decoded = decodeProjectItemRouteSearch(search);
    return decoded.threadId ? { threadId: convexThreadId(decoded.threadId) } : {};
  } catch {
    return {};
  }
}

export function parseConsoleApiProjectRouteParams(params: {
  readonly workspaceId: string;
  readonly apiProjectId: string;
}): ConsoleApiProjectRouteParams | false {
  try {
    const decoded = decodeConsoleApiProjectRouteParams(params);
    return {
      workspaceId: convexWorkspaceId(decoded.workspaceId),
      apiProjectId: convexApiProjectId(decoded.apiProjectId),
    };
  } catch {
    return false;
  }
}
